import {
  DialogActions,
  Button,
  DialogContent,
  Grid,
  DialogTitle,
  FormControl,
  FormHelperText,
  Box,
  Checkbox,
  FormLabel,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import { Alert } from "@mui/material";
import FieldRichText from "components/FieldRichText";
import SelectProfession from "components/SelectProfession";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useMemo } from "react";
import { Field, Form } from "react-final-form";
import { useIntl, FormattedMessage as T } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  CertificateTemplatesOverride,
  CertificateTemplatesOverrideRequest,
} from "services/KnowfullyAdminClient";
import { useQueryClient } from "react-query";
import { UseBooleanActions } from "hooks/useBoolean";
import createDecorator from "final-form-calculate";
import { Decorator } from "final-form";

const useStyles = makeStyles(() => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: "10px",
  },
}));

type CertificateTemplateProfessionFormValues = {
  id?: string;
  professionId: string;
  professionName: string;
  templateHtmlOverride: string;
  isCertificateOfCompletion: boolean;
};

type CertificateTemplateProfessionFormProps = {
  certificateTemplateId: string;
  certificateTemplatesOverride?: CertificateTemplatesOverride;
  openActions: UseBooleanActions;
  onSavedSuccessfully: () => void;
  onProfessionRepeated: () => void;
};

const CertificateTemplateProfessionForm = ({
  certificateTemplateId,
  certificateTemplatesOverride,
  openActions,
  onSavedSuccessfully,
  onProfessionRepeated,
}: CertificateTemplateProfessionFormProps) => {
  const { certificateService, certificateTemplateEditUrl } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const professionCalculator = useMemo(() => {
    return createDecorator(
      ...[
        {
          field: "isCertificateOfCompletion",
          updates: {
            professionId: (
              isCertificateOfCompletionValue: string,
              allValues: any
            ) => {
              return isCertificateOfCompletionValue
                ? ""
                : allValues.professionId;
            },
          },
        },
      ]
    ) as Decorator<CertificateTemplateProfessionFormValues>;
  }, []);

  const init = useMemo(
    () => ({
      certificateTemplateId: certificateTemplateId,
      isCertificateOfCompletion: certificateTemplatesOverride?.professionId
        ? false
        : certificateTemplatesOverride?.professionId === null,
      ...pick(certificateTemplatesOverride ?? {}, [
        "id",
        "professionName",
        "professionId",
        "templateHtmlOverride",
      ]),
    }),
    [certificateTemplateId, certificateTemplatesOverride]
  );

  return useMemo(() => {
    const onSubmit = async (
      values: CertificateTemplateProfessionFormValues
    ) => {
      try {
        await certificateService.saveCertificateTemplatesOverride(
          certificateTemplateId,
          new CertificateTemplatesOverrideRequest(values)
        );

        queryClient.invalidateQueries([
          "certificateTemplatesOverrides",
          certificateTemplateId,
        ]);

        navigate(certificateTemplateEditUrl(certificateTemplateId));
        openActions.setFalse();
        onSavedSuccessfully();
      } catch (error: any) {
        console.log(error);
        onProfessionRepeated();
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        decorators={[professionCalculator]}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              <T id={"certificateTemplateProfessionsDialogTitleKey"} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <Field name="professionId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProfession
                            {...input}
                            label={t({
                              id: "professionLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            disabled={values.isCertificateOfCompletion}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={4}>
                  <Field name="isCertificateOfCompletion" type="checkbox">
                    {({ input }) => (
                      <Box sx={styles.lineContainer}>
                        <Checkbox {...input} data-cy="certificateOfCompletionCheckBox"/>
                        <FormLabel>
                          <T id="certificateCompletionLabel" />
                        </FormLabel>
                      </Box>
                    )}
                  </Field>
                </Grid>

                <Field name="templateHtmlOverride">
                  {({ input, meta }) => (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                      <FieldRichText
                        input={input}
                        meta={meta}
                        label={t({
                          id: "certificateTemplateProfessionsOverrideLabel",
                        })}
                        init={{
                          height: 280,
                          paste_data_images: true,
                        }}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={openActions.setFalse} data-cy="cancelButton">
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting} data-cy="saveButton">
                {t({ id: "certificateTemplateSaveProfession" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    certificateTemplateEditUrl,
    certificateTemplateId,
    certificateService,
    styles.feedback,
    navigate,
    init,
    onProfessionRepeated,
    onSavedSuccessfully,
    openActions,
    queryClient,
    t,
    styles.lineContainer,
    professionCalculator,
  ]);
};

export default CertificateTemplateProfessionForm;
